import { Box, Dialog } from "@mui/material";
import { useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { HTTP_RESPONSE_CODE, NOTIFICATION_TYPE } from "../../common/const";
import { validateFields } from "../../common/helper";
import { useDeveloperContext } from "../../hook/useDeveloperContext";
import { ShowProgress } from "../../state/ShowProgress";
import * as yup from "yup";
import { watchAddressApi } from "../../openApi";
import { ThemeLight } from "../../state/ThemeLight";

type Props = {
  open: boolean;
  handleOpen: (open: boolean) => void;
  handleRefresh: (resetPageNum?: boolean) => void;
};

export const AddWatchItemModal = ({
  open,
  handleOpen,
  handleRefresh,
}: Props) => {
  const { userID, sessionKey, handleSessionKey } = useDeveloperContext();

  const [, setShowProgress] = useRecoilState(ShowProgress);
  const [themeLight] = useRecoilState(ThemeLight);

  const [address, setAddress] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [notificationType, setNotificationType] = useState<number>(
    NOTIFICATION_TYPE.NONE
  );
  const [trackERC20, setTrackERC20] = useState<boolean>(false);
  const [trackERC721, setTrackERC721] = useState<boolean>(false);
  const [trackERC1155, setTrackERC1155] = useState<boolean>(false);

  const handleAdd = async () => {
    try {
      await validateFields(
        yup.object().shape({
          address: yup.string().required("Please input the address."),
        }),
        ["address"],
        [address]
      );
    } catch (err: any) {
      toast.warn(err.message, { theme: themeLight ? "light" : "dark" });
      return;
    }

    setShowProgress(true);
    watchAddressApi
      .addAddress(userID, {
        accessToken: sessionKey!,
        address,
        description,
        notificationType,
        trackERC20Transfer: trackERC20,
        trackERC721Transfer: trackERC721,
        trackERC1155Transfer: trackERC1155,
      })
      .then((res) => {
        setShowProgress(false);

        toast.success("Watch address has been added successfully.", {
          theme: themeLight ? "light" : "dark",
        });
        handleRefresh(true);
        handleOpen(false);
      })
      .catch((err) => {
        setShowProgress(false);
        if (err.response) {
          if (
            err.response.data.statusCode === HTTP_RESPONSE_CODE.UNAUTHORIZED
          ) {
            toast.error("Session key is expired", {
              theme: themeLight ? "light" : "dark",
            });
            handleSessionKey("");
          } else {
            toast.error(err.response.data.message, {
              theme: themeLight ? "light" : "dark",
            });
          }
        } else {
          toast.error(err.message, { theme: themeLight ? "light" : "dark" });
        }
      });
  };

  return (
    <Dialog open={open} onClose={() => handleOpen(false)} fullWidth>
      <Box
        className="modal modal-content"
        sx={{ borderRadius: "5px !important" }}
      >
        <Box className="modal-header">
          <h5
            id="ContentPlaceHolder1_myresponsiveLabel"
            className="modal-title"
          >
            &nbsp;Add New Address
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => handleOpen(false)}
          ></button>
        </Box>
        <Box className="modal-body">
          <Box className="mb-4">
            <Box className="form-group">
              <label className="form-label">UPCX Address</label>
              <Box className="js-form-message">
                <input
                  name="ctl00$ContentPlaceHolder1$txtAddress"
                  type="text"
                  id="ContentPlaceHolder1_txtAddress"
                  className="form-control py-2"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </Box>
            </Box>
          </Box>
          <Box className="mb-4">
            <Box className="form-group">
              <label className="form-label">
                Description <span className="text-muted">(Optional)</span>
              </label>
              <textarea
                name="ctl00$ContentPlaceHolder1$txtDescription"
                id="ContentPlaceHolder1_txtDescription"
                className="js-count-characters form-control py-2"
                placeholder="Short description.."
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, 300))}
              ></textarea>
              <Box className="form-text mt-2">
                Character limit:{" "}
                <span id="maxLengthCountCharactersEdit" className="text-muted">
                  {description.length} / 300
                </span>
                .
              </Box>
            </Box>
          </Box>
          <h4 className="h6">Notification Methods</h4>
          <p>
            You can monitor and receive an alert to your email when an address
            on your watchlist sends or receives any transactions.
          </p>
          <Box className="form-check mb-4">
            <input
              value="RadioButton0"
              name="ctl00$ContentPlaceHolder1$NotificationType"
              type="radio"
              id="ContentPlaceHolder1_RadioButton0"
              className="form-check-input"
              checked={notificationType === NOTIFICATION_TYPE.NONE}
              readOnly
              onClick={() => setNotificationType(NOTIFICATION_TYPE.NONE)}
            />
            <label className="form-check-label">No Notification</label>
          </Box>
          <Box className="form-check mb-4">
            <input
              value="RadioButton1"
              name="ctl00$ContentPlaceHolder1$NotificationType"
              type="radio"
              id="ContentPlaceHolder1_RadioButton1"
              className="form-check-input"
              checked={notificationType === NOTIFICATION_TYPE.INCOME_OUTGO}
              onClick={() =>
                setNotificationType(NOTIFICATION_TYPE.INCOME_OUTGO)
              }
              readOnly
            />
            <label className="form-check-label">
              Notify on Incoming &amp; Outgoing Txns
            </label>
          </Box>
          <Box className="form-check mb-4">
            <input
              value="RadioButton2"
              name="ctl00$ContentPlaceHolder1$NotificationType"
              type="radio"
              id="ContentPlaceHolder1_RadioButton2"
              className="form-check-input"
              checked={notificationType === NOTIFICATION_TYPE.INCOME}
              onClick={() => setNotificationType(NOTIFICATION_TYPE.INCOME)}
              readOnly
            />
            <label className="form-check-label">
              Notify on Incoming (Receive) Txns Only
            </label>
          </Box>
          <Box className="form-check mb-4">
            <input
              value="RadioButton3"
              name="ctl00$ContentPlaceHolder1$NotificationType"
              type="radio"
              id="ContentPlaceHolder1_RadioButton3"
              className="form-check-input"
              checked={notificationType === NOTIFICATION_TYPE.OUTGO}
              onClick={() => setNotificationType(NOTIFICATION_TYPE.OUTGO)}
              readOnly
            />
            <label className="form-check-label">
              Notify on Outgoing (Sent) Txns Only
            </label>
          </Box>
          <h4 className="h6">Other Options</h4>
          <Box className="form-check mb-4">
            <input
              name="ctl00$ContentPlaceHolder1$chkTokenTransfer"
              type="checkbox"
              id="ContentPlaceHolder1_chkTokenTransfer"
              className="form-check-input"
              checked={trackERC20}
              onChange={(e) => setTrackERC20(e.target.checked)}
              readOnly
            />
            <label className="form-check-label">
              Also Track ERC20 Token Transfers
            </label>
          </Box>
          <Box className="form-check mb-4">
            <input
              name="ctl00$ContentPlaceHolder1$chkToken721Transfer"
              type="checkbox"
              id="ContentPlaceHolder1_chkToken721Transfer"
              className="form-check-input"
              checked={trackERC721}
              onChange={(e) => setTrackERC721(e.target.checked)}
              readOnly
            />
            <label className="form-check-label">
              Also Track ERC721 Token Transfers
            </label>
          </Box>
          <Box className="form-check mb-4">
            <input
              name="ctl00$ContentPlaceHolder1$chkToken1155Transfer"
              type="checkbox"
              id="ContentPlaceHolder1_chkToken1155Transfer"
              className="form-check-input"
              checked={trackERC1155}
              onChange={(e) => setTrackERC1155(e.target.checked)}
              readOnly
            />
            <label className="form-check-label">
              Also Track ERC1155 Token Transfers
            </label>
          </Box>
        </Box>
        <Box className="modal-footer bg-light">
          <a
            className="btn btn-ghost-white cancel"
            type="button"
            onClick={() => handleOpen(false)}
          >
            Cancel
          </a>
          <input
            type="submit"
            name="ctl00$ContentPlaceHolder1$btnSubmit"
            value="Add"
            id="ContentPlaceHolder1_btnSubmit"
            className="btn btn-primary"
            onClick={handleAdd}
          />
        </Box>
      </Box>
    </Dialog>
  );
};
